import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { USBButton } from '@usb-shield/forms-beta';
import {
    Header,
    Footer,
    FormBuilder,
} from '@usb-onboarding/product-recommender-components';
import NotFound from '../NotFound';
import {
    sendQuestion,
    resetQuestionnaire,
    removeLastQuestion,
    updateCurrentQuestionnaire,
} from '../../redux/actions';
import {
    productConfiguration,
    tealiumConfiguration,
} from '../../config';
import { productID } from '../../constants';
import {
    buildFormSchema,
    getGeneralReportingData,
    dispatchReportingEvent,
    tealiumUpdatedPageView,
} from '../../utils';
import Loader from '../../atoms/Loader';
import styles from './questionnaire.module.scss';

function Questionnaire() {
    const [locationKeys, setLocationKeys] = useState([]);
    const formRef = useRef();
    const history = useHistory();
    const dispatch = useDispatch();

    const error = useSelector((state) => state.errorReducer.error);
    const AEMContent = useSelector(
        (state) => state.AEMContentReducer.AEMContent,
    );
    const sessionID = useSelector(
        (state) => state.questionnaireReducer.sessionID,
    );
    const questionnaireState = useSelector(
        (state) => state.questionnaireReducer.questionnaireState,
    );
    const currentQuestionnaire = useSelector(
        (state) => state.questionnaireReducer.questionnaire,
    );
    const questionID = useSelector(
        (state) => state.questionnaireReducer.questionnaire?.id,
    );

    const nextButtonText =
        questionID === 'Q8' ? 'See my recommendations' : 'Next';

    const questionNumber = questionID
        ? questionID.substring(1)
        : null;
    const goBackNav = useSelector(
        (state) => state.questionnaireReducer.goBack,
    );
    const isLoading = useSelector(
        (state) => state.loaderReducer.isLoading,
    );

    function goBack() {
        if (questionnaireState.length > 0) {
            dispatch(updateCurrentQuestionnaire());
            dispatch(removeLastQuestion());
        } else {
            dispatch(resetQuestionnaire());
        }
    }

    function customSubmit() {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    }

    function onSubmit(values, { setSubmitting }) {
        const payload = {
            productID: productConfiguration[productID],
            sessionID,
            questionID,
            customerID: sessionID,
            answerInfo: values,
        };

        dispatch(sendQuestion(payload));
        setSubmitting(false);
    }

    if (productConfiguration[productID]) {
        useEffect(
            () =>
                history.listen((location) => {
                    if (history.action === 'PUSH') {
                        setLocationKeys([location.key]);
                    }

                    if (history.action === 'POP') {
                        setLocationKeys((keys) => [
                            location.key,
                            ...keys,
                        ]);
                        goBack();
                    }
                }),
            [locationKeys],
        );

        useEffect(() => {
            if (!sessionID) {
                history.push('/start');
            } else if (currentQuestionnaire) {
                const tealiumConfig = tealiumConfiguration[productID];
                const { siteSection, subSiteSection } = tealiumConfig;

                const {
                    metaData: { question },
                } = currentQuestionnaire;

                const currentPage = question
                    .toLowerCase()
                    .replace('?', '');

                const reportingData = getGeneralReportingData(
                    currentPage,
                    siteSection,
                    subSiteSection,
                );
                dispatchReportingEvent(reportingData);
                tealiumUpdatedPageView();

                history.push(`/questionnaire/${questionID}`);
            } else if (!currentQuestionnaire) {
                history.push('/recommended-products');
            }
        }, [sessionID, currentQuestionnaire, goBackNav]);

        useEffect(() => {
            if (currentQuestionnaire) {
                window.scrollTo(0, 0);
            }
        }, [currentQuestionnaire]);

        useEffect(() => {
            if (error) {
                history.push('/tech-trouble');
            }
        }, [error]);

        return (
            <>
                {isLoading && <Loader />}
                <Header title={AEMContent.header.fullName} />
                {questionNumber ? (
                    <Helmet>
                        <title>
                            Question {questionNumber} | Product
                            recommender | U.S. Bank
                        </title>
                    </Helmet>
                ) : (
                    ''
                )}
                <main
                    id="app__content"
                    className={styles['main-content-wrapper']}
                >
                    <div className={styles['main-left-content']}>
                        <FormBuilder
                            schema={buildFormSchema(
                                productConfiguration[productID],
                                `Form for ${productConfiguration[productID]}`,
                                currentQuestionnaire,
                            )}
                            innerRef={formRef}
                            onSubmit={onSubmit}
                        />
                        <div className={styles['button-container']}>
                            <USBButton
                                variant="text"
                                ariaLabel="Return to previous page"
                                onSubmit={goBack}
                            >
                                Back
                            </USBButton>

                            <USBButton
                                variant="primary"
                                ariaLabel="Start questionnaire"
                                onSubmit={customSubmit}
                            >
                                {nextButtonText}
                            </USBButton>
                        </div>
                    </div>
                </main>
                <Footer
                    header={AEMContent.footer.connectionTitle}
                    content={AEMContent.footer.footerDisclosure}
                    privacyLabel={AEMContent.footer.privacyLabel}
                    privacyUrl={AEMContent.footer.privacyUrl}
                    fdic={AEMContent.footer.connectionContent}
                    cobrowseLabel={AEMContent.footer.cobrowseLabel}
                    cobrowseUrl={AEMContent.footer.cobrowseUrl}
                />
            </>
        );
    }
    return <NotFound />;
}

export default Questionnaire;
